import { useEffect, useState } from "react";
import { wfAPI } from "./wfApi";
import { v2API } from "./v2Api";
import { toast } from "sonner";
import VisualAccessManager from "./VisualAccessManager";
import { X, Ban, RotateCcw, Save, Shield, KeyRound, Copy, RefreshCw, Pencil, Check, ShieldCheck, UserX, Unlock, CheckCircle2, ShieldOff } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";

// Generate a strong temp password: 12 chars with letters, digits, and symbols
function generateTempPassword(len = 12) {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digits = "23456789";
  const symbols = "!@#$%^&*";
  const all = upper + lower + digits + symbols;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let pwd = [pick(upper), pick(lower), pick(digits), pick(symbols)];
  for (let i = pwd.length; i < len; i++) pwd.push(pick(all));
  // Fisher-Yates shuffle
  for (let i = pwd.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pwd[i], pwd[j]] = [pwd[j], pwd[i]];
  }
  return pwd.join("");
}

export default function EmployeeDrawer({ employeeId, onClose }) {
  const [emp, setEmp] = useState(null);
  const [employments, setEmployments] = useState([]);
  const [temporary, setTemporary] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [caps, setCaps] = useState([]);
  const [tab, setTab] = useState("access");
  const [assignForm, setAssignForm] = useState({ role_template_id: "", permission_profile_id: "" });
  const [tempForm, setTempForm] = useState({ capability_keys: [], effective_from: "", effective_until: "", reason: "" });
  const [resetOpen, setResetOpen] = useState(false);

  // Inline edit — Overview tab (personal details)
  const [editingOverview, setEditingOverview] = useState(false);
  const [overviewForm, setOverviewForm] = useState({});
  const [savingOverview, setSavingOverview] = useState(false);

  // Inline edit — Employment cards
  const [editingEmpId, setEditingEmpId] = useState(null);
  const [empForm, setEmpForm] = useState({});
  const [savingEmp, setSavingEmp] = useState(false);
  const [orgUnits, setOrgUnits] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  // WF-3 · System Access panel state (in Access tab)
  const [access, setAccess] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const advancedMode = (() => { try { return localStorage.getItem("wf_advanced") === "1"; } catch { return false; } })();

  const load = () => {
    Promise.all([
      wfAPI.employee(employeeId),
      wfAPI.employments(employeeId),
      wfAPI.temporaryList(employeeId),
      wfAPI.roleTemplates(),
      wfAPI.capabilities(),
      v2API.systemAccess().catch(() => []),
    ]).then(([e, es, tp, rts, cs, sa]) => {
      setEmp(e); setEmployments(es); setTemporary(tp); setRoleTemplates(rts); setCaps(cs);
      // Find this user's access row (if login exists)
      const row = (sa || []).find((r) => r.user_id === employeeId) || null;
      setAccess(row);
      setAllEmployees(sa || []);
    }).catch((err) => toast.error(err?.response?.data?.detail || "Failed to load employee"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);

  // Lazy-load Org Units + Job Titles the first time an employment enters edit mode
  useEffect(() => {
    if (editingEmpId && orgUnits.length === 0) {
      wfAPI.orgUnits().then(setOrgUnits).catch(() => {});
    }
    if (editingEmpId && jobTitles.length === 0) {
      wfAPI.jobTitles().then(setJobTitles).catch(() => {});
    }
  }, [editingEmpId, orgUnits.length, jobTitles.length]);

  useEffect(() => {
    if (!assignForm.role_template_id) { setProfiles([]); return; }
    wfAPI.permissionProfiles(assignForm.role_template_id).then(setProfiles).catch(() => setProfiles([]));
  }, [assignForm.role_template_id]);

  const assignRole = async () => {
    try {
      await wfAPI.assignRole({
        employee_id: employeeId,
        role_template_id: assignForm.role_template_id,
        permission_profile_id: assignForm.permission_profile_id,
      });
      toast.success("Role assigned");
      setAssignForm({ role_template_id: "", permission_profile_id: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to assign"); }
  };

  const grantTemp = async () => {
    if (!tempForm.capability_keys.length || !tempForm.effective_until || !tempForm.reason) {
      toast.error("Pick at least one capability, an end date, and a reason.");
      return;
    }
    if (tempForm.effective_from && tempForm.effective_from > tempForm.effective_until) {
      toast.error("Start date cannot be after the end date.");
      return;
    }
    try {
      await wfAPI.temporaryCreate({
        ...tempForm,
        effective_from: tempForm.effective_from || null,
        employee_id: employeeId,
      });
      toast.success("Temporary access granted");
      setTempForm({ capability_keys: [], effective_from: "", effective_until: "", reason: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEditOverview = () => {
    setOverviewForm({
      name: emp.name || "",
      phone: emp.phone || "",
      preferred_language: emp.preferred_language || "en",
      government_id: emp.government_id || "",
      emergency_contact_name: emp.emergency_contact?.name || "",
      emergency_contact_phone: emp.emergency_contact?.phone || "",
      emergency_contact_relation: emp.emergency_contact?.relation || "",
    });
    setEditingOverview(true);
  };

  const saveOverview = async () => {
    if (!overviewForm.name?.trim()) { toast.error("Name is required"); return; }
    setSavingOverview(true);
    try {
      const patch = {
        name: overviewForm.name.trim(),
        phone: overviewForm.phone || null,
        preferred_language: overviewForm.preferred_language || null,
        government_id: overviewForm.government_id || null,
      };
      if (overviewForm.emergency_contact_name || overviewForm.emergency_contact_phone) {
        patch.emergency_contact = {
          name: overviewForm.emergency_contact_name || null,
          phone: overviewForm.emergency_contact_phone || null,
          relation: overviewForm.emergency_contact_relation || null,
        };
      }
      await wfAPI.updateEmployee(employeeId, patch);
      toast.success("Details updated");
      setEditingOverview(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    finally { setSavingOverview(false); }
  };

  const startEditEmployment = (empmt) => {
    setEmpForm({
      org_unit_id: empmt.org_unit_id || "",
      job_title_id: empmt.job_title_id || "",
      job_title_name: empmt.job_title_name || "",
      position: empmt.position || "",
      reporting_manager_id: empmt.reporting_manager_id || "",
      date_joined: empmt.start_date || "",
      effective_until: empmt.end_date || "",
    });
    setEditingEmpId(empmt.id);
  };

  const saveEmployment = async () => {
    setSavingEmp(true);
    try {
      const patch = {
        org_unit_id: empForm.org_unit_id || null,
        job_title_id: empForm.job_title_id || null,
        job_title_name: !empForm.job_title_id && empForm.job_title_name ? empForm.job_title_name : null,
        position: empForm.position || null,
        reporting_manager_id: empForm.reporting_manager_id || null,
        date_joined: empForm.date_joined || null,
        effective_until: empForm.effective_until || null,
      };
      const clean = Object.fromEntries(
        Object.entries(patch).filter(([, v]) => v !== undefined)
      );
      await wfAPI.updateEmployment(editingEmpId, clean);
      toast.success("Employment updated");
      setEditingEmpId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    finally { setSavingEmp(false); }
  };

  const terminate = async () => {
    if (!window.confirm(`Terminate ${emp?.name}? This revokes all active roles and sessions.`)) return;
    try {
      await wfAPI.terminateEmployee(employeeId, "Terminated via drawer");
      toast.success("Employee terminated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reactivate = async () => {
    try { await wfAPI.reactivateEmployee(employeeId); toast.success("Reactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const permanentlyDelete = async () => {
    if (!window.confirm(
      `Permanently delete ${emp?.name}?\n\n` +
      `This CANNOT be undone. The user, all employments, sessions and access ` +
      `will be erased. The email address becomes reusable for a brand-new employee.`,
    )) return;
    try {
      const r = await wfAPI.permanentlyDeleteEmployee(employeeId);
      toast.success(`Deleted permanently. Email "${r.email}" is now free to reuse.`);
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (!emp) {
    return (
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose}>
        <div className="absolute right-0 top-0 h-full w-[560px] max-w-full bg-white shadow-2xl p-6 text-slate-400 text-[13px]">Loading…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} data-testid="employee-drawer">
      <div className="absolute right-0 top-0 h-full w-[600px] max-w-full bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-slate-200 flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 font-semibold text-[15px] shrink-0">
            {(emp.name || emp.email || "?").slice(0,2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-[18px] font-semibold text-hocp-navy truncate">{emp.name}</h2>
            <div className="text-[12.5px] text-slate-500 truncate">{emp.email}</div>
            <div className="mt-2 flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-md text-[11.5px] font-medium ${
                emp.employment_status === "active" ? "bg-emerald-50 text-emerald-700"
                  : emp.employment_status === "suspended" ? "bg-amber-50 text-amber-700"
                  : "bg-rose-50 text-rose-700"}`}>{emp.employment_status}</span>
              {emp.employee_id && <span className="text-[11.5px] mono text-slate-500">{emp.employee_id}</span>}
            </div>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="p-2 rounded-lg hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500"/>
          </button>
        </div>

        <div className="px-6 border-b border-slate-200 flex gap-1">
          {(() => {
            const tabs = [
              ["overview","Overview"],
              ["access","Access"],
              ["employments","Employments"],
            ];
            if (advancedMode) {
              tabs.push(["permissions","Roles (Advanced)"]);
              tabs.push(["temporary","Temporary"]);
            }
            return tabs;
          })().map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`drawer-tab-${k}`}
              className={`px-3 py-2.5 text-[13px] font-medium border-b-2 transition-colors ${
                tab === k ? "border-hocp-navy text-hocp-navy" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {tab === "overview" && (
            <div className="space-y-1" data-testid="drawer-overview">
              <div className="flex items-center justify-between pb-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Personal Details</div>
                {!editingOverview ? (
                  <button onClick={startEditOverview} data-testid="overview-edit-btn"
                    className="inline-flex items-center gap-1 text-[12px] text-hocp-navy font-medium hover:underline">
                    <Pencil className="w-3.5 h-3.5"/> Edit
                  </button>
                ) : (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditingOverview(false)} className="text-[12px] text-slate-500 px-2 py-1 hover:text-slate-800">Cancel</button>
                    <button onClick={saveOverview} disabled={savingOverview} data-testid="overview-save-btn"
                      className="inline-flex items-center gap-1 text-[12px] text-hocp-deepTeal bg-hocp-mint px-3 py-1.5 rounded-md font-medium hover:brightness-110 disabled:opacity-60">
                      <Check className="w-3.5 h-3.5"/> {savingOverview ? "Saving…" : "Save"}
                    </button>
                  </div>
                )}
              </div>

              {!editingOverview ? (
                <>
                  <Row k="Name" v={emp.name}/>
                  <Row k="Email" v={emp.email}/>
                  <Row k="Phone" v={emp.phone || "—"}/>
                  <Row k="Language" v={emp.preferred_language || "—"}/>
                  <Row k="Government ID" v={emp.government_id || "—"}/>
                  <Row k="Job Title" v={emp.designation || "—"}/>
                  <Row k="Date joined" v={emp.date_joined || "—"}/>
                  <Row k="Hotels" v={(emp.hotel_ids || []).length + " hotel(s)"}/>
                  <Row k="Active role" v={emp.role_assignments?.[0]?.role_template_name || <span className="text-slate-400">Unassigned</span>}/>
                  {emp.emergency_contact?.name && (
                    <Row k="Emergency" v={`${emp.emergency_contact.name} · ${emp.emergency_contact.phone || "—"} (${emp.emergency_contact.relation || "—"})`}/>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <EditRow label="Name">
                    <input value={overviewForm.name} onChange={e => setOverviewForm({...overviewForm, name: e.target.value})}
                      data-testid="overview-input-name" className={inputCls}/>
                  </EditRow>
                  <EditRow label="Email" hint="Email is immutable"><input value={emp.email} disabled className={inputCls + " opacity-60"}/></EditRow>
                  <EditRow label="Phone">
                    <input value={overviewForm.phone} onChange={e => setOverviewForm({...overviewForm, phone: e.target.value})}
                      data-testid="overview-input-phone" placeholder="+91…" className={inputCls}/>
                  </EditRow>
                  <EditRow label="Language">
                    <select value={overviewForm.preferred_language}
                      onChange={e => setOverviewForm({...overviewForm, preferred_language: e.target.value})}
                      data-testid="overview-input-language" className={inputCls}>
                      <option value="en">English</option>
                      <option value="hi">Hindi</option>
                      <option value="bn">Bengali</option>
                      <option value="ta">Tamil</option>
                      <option value="te">Telugu</option>
                      <option value="mr">Marathi</option>
                      <option value="kn">Kannada</option>
                      <option value="gu">Gujarati</option>
                    </select>
                  </EditRow>
                  <EditRow label="Government ID">
                    <input value={overviewForm.government_id} onChange={e => setOverviewForm({...overviewForm, government_id: e.target.value})}
                      data-testid="overview-input-govid" placeholder="Aadhaar / PAN / passport" className={inputCls}/>
                  </EditRow>
                  <div className="pt-2 mt-2 border-t border-slate-100">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-2">Emergency Contact</div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={overviewForm.emergency_contact_name} onChange={e => setOverviewForm({...overviewForm, emergency_contact_name: e.target.value})}
                        data-testid="overview-input-ec-name" placeholder="Name" className={inputCls}/>
                      <input value={overviewForm.emergency_contact_phone} onChange={e => setOverviewForm({...overviewForm, emergency_contact_phone: e.target.value})}
                        data-testid="overview-input-ec-phone" placeholder="Phone" className={inputCls}/>
                      <input value={overviewForm.emergency_contact_relation} onChange={e => setOverviewForm({...overviewForm, emergency_contact_relation: e.target.value})}
                        data-testid="overview-input-ec-relation" placeholder="Relation" className={inputCls + " col-span-2"}/>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "access" && (
            <AccessTab
              employeeId={employeeId}
              emp={emp}
              access={access}
              allEmployees={allEmployees}
              onRefresh={load}
            />
          )}

          {tab === "permissions" && (
            <>
              <div>
                <h3 className="font-display text-[14px] font-semibold text-hocp-navy mb-3">Active role assignments</h3>
                {!emp.role_assignments?.length ? (
                  <div className="text-slate-400 text-[13px]">None</div>
                ) : (
                  <div className="space-y-2">
                    {emp.role_assignments.filter(ra => ra.status === "active").map((ra) => (
                      <div key={ra.id} className="border border-slate-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-[13px] font-medium text-hocp-navy">{ra.role_template_name}</div>
                            <div className="text-[11.5px] text-slate-400">{ra.permission_profile_code} · {(ra.capabilities || []).length} capabilities</div>
                          </div>
                          <button onClick={async () => { try { await wfAPI.revokeAssignment(ra.id); toast.success("Revoked"); load(); } catch(e){toast.error("Failed");} }}
                            data-testid={`revoke-assignment-${ra.id}`}
                            className="text-[12px] text-rose-600 hover:underline">Revoke</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="pt-4 border-t border-slate-100">
                <h3 className="font-display text-[14px] font-semibold text-hocp-navy mb-3">Assign a new role</h3>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <select value={assignForm.role_template_id} onChange={(e)=>setAssignForm({role_template_id:e.target.value, permission_profile_id:""})}
                    data-testid="assign-role-select"
                    className={inputCls}>
                    <option value="">Role Template</option>
                    {roleTemplates.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                  <select value={assignForm.permission_profile_id} onChange={(e)=>setAssignForm({...assignForm, permission_profile_id:e.target.value})}
                    data-testid="assign-profile-select"
                    className={inputCls} disabled={!profiles.length}>
                    <option value="">Permission Profile</option>
                    {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <button onClick={assignRole} disabled={!assignForm.role_template_id || !assignForm.permission_profile_id}
                  data-testid="assign-role-submit"
                  className="h-10 px-4 rounded-lg bg-hocp-gold text-hocp-navy text-[13px] font-medium disabled:opacity-50 hover:brightness-95 inline-flex items-center gap-2">
                  <Shield className="w-4 h-4"/> Assign role
                </button>
              </div>
            </>
          )}

          {tab === "temporary" && (
            <>
              <div>
                <h3 className="font-display text-[14px] font-semibold text-hocp-navy mb-3">Active temporary grants</h3>
                {!temporary.length ? (
                  <div className="text-slate-400 text-[13px]">None</div>
                ) : (
                  <div className="space-y-2">
                    {temporary.map((t) => (
                      <div key={t.id} className="border border-slate-200 rounded-lg p-3 flex items-start justify-between">
                        <div>
                          <div className="text-[13px] font-medium">{t.capability_keys.join(", ")}</div>
                          <div className="text-[11.5px] text-slate-400">
                            {t.effective_from ? `${(t.effective_from || '').slice(0,10)} → ${(t.effective_until || '').slice(0,10)}` : `Until ${(t.effective_until || '').slice(0,10)}`} — {t.reason}
                          </div>
                        </div>
                        <button onClick={async ()=>{try{await wfAPI.temporaryRevoke(t.id); toast.success("Revoked"); load();}catch(e){toast.error("Failed");}}}
                          className="text-[12px] text-rose-600 hover:underline">Revoke</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="pt-4 border-t border-slate-100">
                <h3 className="font-display text-[14px] font-semibold text-hocp-navy mb-3">Grant temporary access</h3>
                <div className="space-y-3">
                  <select multiple size={5}
                    value={tempForm.capability_keys}
                    onChange={(e) => setTempForm({...tempForm, capability_keys: Array.from(e.target.selectedOptions).map(o=>o.value)})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px]">
                    {caps.map((c) => <option key={c.key} value={c.key}>{c.key} — {c.name}</option>)}
                  </select>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Start date</label>
                      <DatePicker value={(tempForm.effective_from || '').slice(0,10)} onChange={(_v) => setTempForm({...tempForm, effective_from:_v})} data-testid="temp-from"/>
                      <div className="text-[10.5px] text-slate-400 mt-1">Leave empty to start immediately.</div>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">End date</label>
                      <DatePicker value={(tempForm.effective_until || '').slice(0,10)} onChange={(_v) => setTempForm({...tempForm, effective_until:_v})} data-testid="temp-until"/>
                    </div>
                  </div>
                  <input value={tempForm.reason} onChange={(e)=>setTempForm({...tempForm, reason:e.target.value})}
                    className={inputCls} placeholder="Reason" data-testid="temp-reason"/>
                  <button onClick={grantTemp} data-testid="temp-grant-btn" className="h-10 px-4 rounded-lg bg-hocp-gold text-hocp-navy text-[13px] font-medium">Grant temporary access</button>
                </div>
              </div>
            </>
          )}

          {tab === "employments" && (
            <div className="space-y-2" data-testid="drawer-employments">
              {!employments.length ? (
                <div className="text-slate-400 text-[13px]">No employments yet.</div>
              ) : (
                employments.map((e) => {
                  const isEdit = editingEmpId === e.id;
                  return (
                    <div key={e.id} className="border border-slate-200 rounded-lg p-3" data-testid={`employment-card-${e.id}`}>
                      {!isEdit ? (
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium text-slate-800">{e.org_unit_name || e.department_id}</div>
                            <div className="text-[11.5px] text-slate-500 mt-0.5">
                              {e.job_title_name || e.position || "—"} · Joined {e.start_date || "—"}{e.end_date ? ` → ${e.end_date}` : ""}
                            </div>
                            {e.reporting_manager_id && (
                              <div className="text-[11px] text-slate-400 mt-0.5 font-mono">Reports to: {e.reporting_manager_id.slice(0, 8)}…</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`text-[11px] px-2 py-0.5 rounded-md ${e.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{e.status}</span>
                            <button onClick={() => startEditEmployment(e)} data-testid={`employment-edit-${e.id}`}
                              className="inline-flex items-center gap-1 text-[12px] text-hocp-navy font-medium hover:underline">
                              <Pencil className="w-3.5 h-3.5"/>Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between pb-1">
                            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Edit employment</div>
                            <div className="flex items-center gap-1">
                              <button onClick={() => setEditingEmpId(null)} className="text-[12px] text-slate-500 px-2 py-1 hover:text-slate-800">Cancel</button>
                              <button onClick={saveEmployment} disabled={savingEmp} data-testid={`employment-save-${e.id}`}
                                className="inline-flex items-center gap-1 text-[12px] text-hocp-deepTeal bg-hocp-mint px-3 py-1.5 rounded-md font-medium hover:brightness-110 disabled:opacity-60">
                                <Check className="w-3.5 h-3.5"/>{savingEmp ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="col-span-2">
                              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Department</label>
                              <select value={empForm.org_unit_id} onChange={ev => setEmpForm({...empForm, org_unit_id: ev.target.value})}
                                data-testid={`employment-input-orgunit-${e.id}`} className={inputCls}>
                                {orgUnits.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Job title</label>
                              <select value={empForm.job_title_id} onChange={ev => setEmpForm({...empForm, job_title_id: ev.target.value})}
                                data-testid={`employment-input-jobtitle-${e.id}`} className={inputCls}>
                                <option value="">— Custom —</option>
                                {jobTitles.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Custom title</label>
                              <input value={empForm.job_title_name} onChange={ev => setEmpForm({...empForm, job_title_name: ev.target.value})}
                                data-testid={`employment-input-customtitle-${e.id}`}
                                disabled={!!empForm.job_title_id}
                                placeholder="Only if no standard title" className={inputCls}/>
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Position</label>
                              <input value={empForm.position} onChange={ev => setEmpForm({...empForm, position: ev.target.value})}
                                data-testid={`employment-input-position-${e.id}`}
                                placeholder="Senior / Lead / Manager" className={inputCls}/>
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Reports to (User ID)</label>
                              <input value={empForm.reporting_manager_id} onChange={ev => setEmpForm({...empForm, reporting_manager_id: ev.target.value})}
                                data-testid={`employment-input-manager-${e.id}`}
                                placeholder="employee id (optional)" className={inputCls + " font-mono text-[11px]"}/>
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Date joined</label>
                              <DatePicker value={(empForm.date_joined || '').slice(0,10)} onChange={(_v) => setEmpForm({...empForm, date_joined: _v})} data-testid={`employment-input-datejoined-${e.id}`}/>
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Effective until</label>
                              <DatePicker value={(empForm.effective_until || '').slice(0,10)} onChange={(_v) => setEmpForm({...empForm, effective_until: _v})} data-testid={`employment-input-until-${e.id}`}/>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-6 py-3 bg-slate-50 flex items-center justify-end gap-2">
          {emp.is_active && (
            <button onClick={() => setResetOpen(true)} data-testid="drawer-reset-password"
              className="h-9 px-3.5 rounded-lg text-hocp-navy text-[13px] font-medium hover:bg-slate-100 inline-flex items-center gap-2">
              <KeyRound className="w-4 h-4"/> Reset Password
            </button>
          )}
          {emp.is_active ? (
            <button onClick={terminate} data-testid="drawer-terminate"
              className="h-9 px-3.5 rounded-lg text-rose-700 text-[13px] font-medium hover:bg-rose-50 inline-flex items-center gap-2">
              <Ban className="w-4 h-4"/> Terminate
            </button>
          ) : (
            <>
              <button onClick={reactivate} data-testid="drawer-reactivate"
                className="h-9 px-3.5 rounded-lg text-emerald-700 text-[13px] font-medium hover:bg-emerald-50 inline-flex items-center gap-2">
                <RotateCcw className="w-4 h-4"/> Reactivate
              </button>
              <button onClick={permanentlyDelete} data-testid="drawer-delete-permanent"
                className="h-9 px-3.5 rounded-lg text-white bg-rose-600 hover:bg-rose-700 text-[13px] font-medium inline-flex items-center gap-2"
                title="Erase this user forever. The email will be free to reuse.">
                <Ban className="w-4 h-4"/> Delete Permanently
              </button>
            </>
          )}
        </div>
      </div>

      {resetOpen && (
        <ResetPasswordModal
          employee={emp}
          onClose={() => setResetOpen(false)}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Reset Password Modal — admin types a new password (or auto-generates),
// backend forces password change on next login and revokes sessions.
// ──────────────────────────────────────────────────────────────────────
function ResetPasswordModal({ employee, onClose }) {
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [force, setForce] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const gen = () => {
    const p = generateTempPassword(12);
    setPwd(p); setConfirm(p);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(pwd); setCopied(true); toast.success("Password copied"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed"); }
  };

  const submit = async () => {
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pwd !== confirm) { toast.error("Passwords do not match"); return; }
    if (!reason.trim()) { toast.error("Reason is required for the audit trail"); return; }
    setSaving(true);
    try {
      await wfAPI.resetEmployeePassword(employee.id, {
        new_password: pwd, reason: reason.trim(),
        force_password_change: force,
      });
      toast.success(`Password reset for ${employee.name}. Share the new password with them privately.`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset password");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="reset-password-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
            <KeyRound className="w-4 h-4 text-amber-700"/>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-[16px] font-semibold text-hocp-navy">Reset password</h3>
            <p className="text-[12px] text-slate-500 truncate">For {employee.name} · {employee.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="reset-password-close">
            <X className="w-4 h-4 text-slate-500"/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium">New password</label>
              <button type="button" onClick={gen} data-testid="reset-password-generate"
                className="text-[11.5px] text-hocp-navy hover:underline inline-flex items-center gap-1">
                <RefreshCw className="w-3 h-3"/> Auto-generate
              </button>
            </div>
            <div className="flex gap-2">
              <input value={pwd} onChange={(e) => setPwd(e.target.value)} type="text"
                     data-testid="reset-password-input"
                     placeholder="Minimum 8 characters"
                     className="flex-1 h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] mono outline-none focus:border-hocp-navy"/>
              {pwd && (
                <button type="button" onClick={copy} data-testid="reset-password-copy"
                  className="h-10 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-[12px] inline-flex items-center gap-1.5">
                  <Copy className="w-3.5 h-3.5"/> {copied ? "Copied" : "Copy"}
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium block mb-1.5">Confirm password</label>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} type="text"
                   data-testid="reset-password-confirm"
                   className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] mono outline-none focus:border-hocp-navy"/>
          </div>

          <div>
            <label className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium block mb-1.5">Reason (audit)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} type="text"
                   data-testid="reset-password-reason"
                   placeholder="e.g. Forgot password, requested by manager"
                   className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy"/>
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}
                   data-testid="reset-password-force"
                   className="mt-0.5"/>
            <div>
              <div className="text-[13px] text-hocp-navy font-medium">Force password change on next login</div>
              <div className="text-[11.5px] text-slate-500">Recommended. The staff must set their own password before entering the app.</div>
            </div>
          </label>

          <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2.5 text-[12px] text-amber-900">
            All active sessions for this user will be revoked. Share the new password with them privately (chat / verbally). It is not sent by email.
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="reset-password-submit"
            className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium disabled:opacity-60 hover:brightness-95 inline-flex items-center gap-2">
            <Save className="w-4 h-4"/> {saving ? "Resetting…" : "Reset Password"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy transition";

// ══════════════════════════════════════════════════════════════════════
// WF-3 · Access Tab · System Login controls + Visual Access Manager
// ══════════════════════════════════════════════════════════════════════
function AccessTab({ employeeId, emp, access, allEmployees, onRefresh }) {
  const [grantOpen, setGrantOpen] = useState(false);
  const [pwdOpen, setPwdOpen]     = useState(false);
  const hasLogin = !!access;

  const doAction = async (fn, msg) => {
    try { await fn(); toast.success(msg); onRefresh?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="space-y-5" data-testid="drawer-access">
      {/* SYSTEM LOGIN card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5"/> System Login
            </div>
            <div className="mt-1 text-[13.5px] font-semibold text-hocp-navy">
              {hasLogin ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"/> Enabled
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-slate-400">
                  <span className="w-2 h-2 rounded-full bg-slate-300"/> No login
                </span>
              )}
            </div>
          </div>
          {!hasLogin ? (
            <button onClick={() => setGrantOpen(true)} data-testid="access-enable-login"
              className="h-9 px-3.5 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium hover:brightness-110 inline-flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5"/> Enable Login
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              {access.identity_status === "pending" && (
                <button onClick={() => doAction(() => v2API.activate(employeeId), "Activated")}
                  data-testid="access-activate"
                  className="h-9 px-3 rounded-lg text-emerald-700 border border-emerald-200 text-[12px] font-medium hover:bg-emerald-50 inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5"/> Activate
                </button>
              )}
              {access.identity_status === "active" && (
                <button onClick={() => doAction(() => v2API.disable(employeeId), "Disabled")}
                  data-testid="access-disable"
                  className="h-9 px-3 rounded-lg text-rose-700 border border-rose-200 text-[12px] font-medium hover:bg-rose-50 inline-flex items-center gap-1.5">
                  <UserX className="w-3.5 h-3.5"/> Disable
                </button>
              )}
              {(access.identity_status === "locked" || access.failed_login_count >= 5) && (
                <button onClick={() => doAction(() => v2API.unlock(employeeId), "Unlocked")}
                  data-testid="access-unlock"
                  className="h-9 px-3 rounded-lg text-sky-700 border border-sky-200 text-[12px] font-medium hover:bg-sky-50 inline-flex items-center gap-1.5">
                  <Unlock className="w-3.5 h-3.5"/> Unlock
                </button>
              )}
              <button onClick={() => setPwdOpen(true)} data-testid="access-reset"
                className="h-9 px-3 rounded-lg text-slate-700 border border-slate-200 text-[12px] font-medium hover:bg-slate-50 inline-flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5"/> Reset Password
              </button>
            </div>
          )}
        </div>
        {hasLogin && (
          <div className="grid grid-cols-2 gap-3 text-[12.5px]">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Username</div>
              <div className="mt-0.5 text-slate-800 font-mono truncate">{access.email}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Status</div>
              <div className="mt-0.5 text-slate-800 capitalize">{(access.identity_status || "active").replace("_", " ")}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Last Login</div>
              <div className="mt-0.5 text-slate-800">
                {access.last_login_at ? new Date(access.last_login_at).toLocaleString() : <span className="text-slate-400">Never</span>}
              </div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Capabilities</div>
              <div className="mt-0.5 text-slate-800">
                {access.is_super_admin
                  ? <span className="text-amber-700">All (Super Admin)</span>
                  : <span className="mono">{access.capability_count ?? 0} granted</span>}
              </div>
            </div>
          </div>
        )}
        {hasLogin && !access.is_super_admin && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
            <div className="text-[11.5px] text-slate-500">
              Removing access revokes login. The employee record stays intact.
            </div>
            <button onClick={() => {
              if (!window.confirm("Remove all access for this employee?")) return;
              doAction(() => v2API.removeAccess(employeeId), "Access removed");
            }} data-testid="access-remove"
              className="text-[12px] text-rose-600 font-medium hover:underline inline-flex items-center gap-1">
              <ShieldOff className="w-3.5 h-3.5"/> Remove Access
            </button>
          </div>
        )}
      </div>

      {/* ACCESS CONTROL — Visual Access Manager */}
      {hasLogin && !access.is_super_admin ? (
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5"/> Access Control
          </div>
          <VisualAccessManager
            userId={employeeId}
            allEmployees={allEmployees}
            onSaved={onRefresh}
          />
        </div>
      ) : hasLogin && access.is_super_admin ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-[12.5px] text-amber-900" data-testid="access-superadmin-note">
          Super Admin has unrestricted access to every module. Access cannot be edited via the visual manager.
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-[12.5px] text-slate-500" data-testid="access-nologin-note">
          This employee cannot log in. Enable login first, then grant access using Quick Presets or the module cards.
        </div>
      )}

      {grantOpen && (
        <GrantLoginDialog employeeId={employeeId} emp={emp}
          onClose={() => setGrantOpen(false)} onGranted={() => { setGrantOpen(false); onRefresh?.(); }}/>
      )}
      {pwdOpen && (
        <ResetPasswordModal employee={emp} onClose={() => { setPwdOpen(false); onRefresh?.(); }}/>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Grant Login dialog — attaches a login credential to an employee.
// ══════════════════════════════════════════════════════════════════════
function GrantLoginDialog({ employeeId, emp, onClose, onGranted }) {
  const [pwd, setPwd]       = useState(generateTempPassword(12));
  const [busy, setBusy]     = useState(false);

  const submit = async () => {
    if (pwd.length < 8) { toast.error("Password must be ≥ 8 chars"); return; }
    setBusy(true);
    try {
      await v2API.grantAccess(employeeId, {
        password: pwd, role_template_id: null, copy_from_employee_id: null,
      });
      // v2 puts identity in "pending" state — auto-activate for a cleaner flow.
      await v2API.activate(employeeId);
      toast.success("Login enabled. Share the password privately.");
      onGranted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to enable login"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}
      data-testid="grant-login-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">Enable login</h3>
          <p className="text-[12px] text-slate-500 truncate">{emp?.name} · {emp?.email}</p>
        </div>
        <div className="p-5 space-y-3">
          <label className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium">Temporary password</label>
          <div className="flex gap-2">
            <input value={pwd} onChange={(e) => setPwd(e.target.value)} type="text" data-testid="grant-pwd"
              className="flex-1 h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] mono outline-none focus:border-hocp-navy"/>
            <button type="button" onClick={() => setPwd(generateTempPassword(12))}
              className="h-10 px-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-[12px] inline-flex items-center gap-1.5"
              data-testid="grant-regen">
              <RefreshCw className="w-3.5 h-3.5"/> Regen
            </button>
          </div>
          <div className="text-[11.5px] text-slate-500">
            After login is enabled you&apos;ll be able to grant module access below. The employee must change this password on first login.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="grant-submit"
            className="h-9 px-4 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium hover:brightness-110 disabled:opacity-60 inline-flex items-center gap-2">
            <Save className="w-3.5 h-3.5"/> {busy ? "Enabling…" : "Enable Login"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center gap-4 py-2 border-b border-slate-100 last:border-0">
      <div className="w-32 text-[12px] text-slate-500 uppercase tracking-wide">{k}</div>
      <div className="text-[13px] text-slate-800 font-medium">{v}</div>
    </div>
  );
}

function EditRow({ label, hint, children }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] items-center gap-3">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
        {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}
